import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { CheckCircle2, Circle, XCircle, User, Calendar, TrendingUp } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Offboarding = () => {
  const [checklists, setChecklists] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchChecklists();
  }, []);

  const fetchChecklists = async () => {
    try {
      const response = await axios.get(`${API_URL}/offboarding/list`);
      setChecklists(response.data);
    } catch (error) {
      console.error('Error fetching checklists:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateItemStatus = async (checklistId, itemId, status) => {
    try {
      await axios.patch(`${API_URL}/offboarding/${checklistId}/item/${itemId}?status=${status}`);
      toast.success('Status atualizado!');
      fetchChecklists();
    } catch (error) {
      toast.error('Erro ao atualizar status');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'concluido':
        return <CheckCircle2 size={20} className="text-emerald-500" />;
      case 'nao_aplicavel':
        return <XCircle size={20} className="text-gray-400" />;
      default:
        return <Circle size={20} className="text-slate-400" />;
    }
  };

  if (loading) {
    return <div>Carregando...</div>;
  }

  return (
    <div className="space-y-6" data-testid="offboarding-page">
      <div>
        <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
          Offboarding - Checklists de Desligamento
        </h2>
        <p className="text-slate-600 dark:text-slate-400 mt-1">
          Gerencie processos automatizados de desligamento
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-6">
          <div className="flex items-center gap-3">
            <div className="bg-blue-100 dark:bg-blue-900 p-3 rounded-lg">
              <User size={24} className="text-blue-600" />
            </div>
            <div>
              <p className="text-xs font-bold uppercase text-slate-500">Total</p>
              <p className="text-2xl font-bold">{checklists.length}</p>
            </div>
          </div>
        </Card>
        <Card className="p-6">
          <div className="flex items-center gap-3">
            <div className="bg-yellow-100 dark:bg-yellow-900 p-3 rounded-lg">
              <TrendingUp size={24} className="text-yellow-600" />
            </div>
            <div>
              <p className="text-xs font-bold uppercase text-slate-500">Em Andamento</p>
              <p className="text-2xl font-bold">{checklists.filter(c => !c.concluido).length}</p>
            </div>
          </div>
        </Card>
        <Card className="p-6">
          <div className="flex items-center gap-3">
            <div className="bg-emerald-100 dark:bg-emerald-900 p-3 rounded-lg">
              <CheckCircle2 size={24} className="text-emerald-600" />
            </div>
            <div>
              <p className="text-xs font-bold uppercase text-slate-500">Concluídos</p>
              <p className="text-2xl font-bold">{checklists.filter(c => c.concluido).length}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Checklists */}
      <div className="space-y-6">
        {checklists.map((checklist) => (
          <Card key={checklist.id} className="p-6" data-testid={`checklist-${checklist.id}`}>
            {/* Header */}
            <div className="flex items-start justify-between mb-6 pb-4 border-b">
              <div className="flex items-center gap-4">
                <div className="bg-slate-100 dark:bg-slate-800 p-3 rounded-full">
                  <User size={32} className="text-slate-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white">
                    {checklist.employee.nome}
                  </h3>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    {checklist.employee.cargo} - {checklist.employee.setor}
                  </p>
                  <div className="flex items-center gap-2 mt-2">
                    <Calendar size={14} className="text-slate-500" />
                    <p className="text-xs text-slate-500">
                      Demissão: {checklist.data_demissao}
                    </p>
                  </div>
                </div>
              </div>
              <div className="text-right">
                {checklist.concluido ? (
                  <Badge className="bg-emerald-500">Concluído</Badge>
                ) : (
                  <Badge className="bg-yellow-500">Em Andamento</Badge>
                )}
                <p className="text-sm text-slate-600 mt-2">
                  Progresso: {checklist.progresso}
                </p>
                <div className="w-32 bg-slate-200 dark:bg-slate-700 rounded-full h-2 mt-2">
                  <div
                    className="bg-emerald-500 h-2 rounded-full transition-all"
                    style={{ width: `${checklist.percentual}%` }}
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1">{checklist.percentual}%</p>
              </div>
            </div>

            {/* Checklist Items */}
            <div className="space-y-3">
              {checklist.items.map((item) => (
                <div
                  key={item.id}
                  className={`flex items-start gap-4 p-4 rounded-lg border ${
                    item.status === 'concluido'
                      ? 'bg-emerald-50 dark:bg-emerald-950 border-emerald-200 dark:border-emerald-800'
                      : 'bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-800'
                  }`}
                  data-testid={`item-${item.id}`}
                >
                  <div className="flex-shrink-0 mt-1">
                    {getStatusIcon(item.status)}
                  </div>
                  <div className="flex-1">
                    <h4 className={`font-semibold ${item.status === 'concluido' ? 'line-through text-slate-500' : 'text-slate-900 dark:text-white'}`}>
                      {item.titulo}
                    </h4>
                    <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                      {item.descricao}
                    </p>
                    {item.data_conclusao && (
                      <p className="text-xs text-emerald-600 mt-2">
                        ✓ Concluído em {new Date(item.data_conclusao).toLocaleDateString('pt-BR')}
                      </p>
                    )}
                  </div>
                  {item.status === 'pendente' && (
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        className="bg-emerald-500 hover:bg-emerald-600"
                        onClick={() => updateItemStatus(checklist.id, item.id, 'concluido')}
                        data-testid={`complete-${item.id}`}
                      >
                        Concluir
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => updateItemStatus(checklist.id, item.id, 'nao_aplicavel')}
                        data-testid={`skip-${item.id}`}
                      >
                        N/A
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Card>
        ))}
      </div>

      {checklists.length === 0 && (
        <Card className="p-12 text-center">
          <User size={48} className="mx-auto text-slate-400 mb-4" />
          <p className="text-slate-600 dark:text-slate-400">
            Nenhum processo de offboarding em andamento.
          </p>
          <p className="text-sm text-slate-500 mt-2">
            Checklists são criados automaticamente quando um funcionário é marcado como DEMITIDO.
          </p>
        </Card>
      )}
    </div>
  );
};

export default Offboarding;
